/**
 * ============================================
 * AUTO-ADD LINKED PRODUCTS COMPONENT
 * ============================================
 * Automatically adds linked products to cart
 * with strict qty=1 limit and full validation
 *
 * Managed via Theme Customizer
 */

class AutoAddProductsComponent extends HTMLElement {
  constructor() {
    super();


    // Customizer settings
    this.showBadge = this.dataset.showBadge === 'true';
    this.showNotifications = this.dataset.showNotifications === 'true';

    // Configuration parsing
    this.rules = this.parseRules();
    this.availability = this.parseAvailability();
    this.metadata = this.parseMetadata();

    // Validation
    if (!this.validateConfiguration()) {
      console.error('[Auto-Add Products] Invalid configuration, component disabled');
      return;
    }

    // Current cart state
    this.triggerProductsInCart = this.parseTriggerProducts();
    this.linkedProductsInCart = this.parseLinkedProducts();
    this.cartItemsCount = parseInt(this.dataset.cartItemsCount) || 0;
    this.currentCart = null; // Will be populated on first onCartUpdated

    // Loop protection
    this.loopProtection = new InfiniteLoopProtection();
    this.updateQueue = new UpdateQueue();

    // Flags
    this.isProcessing = false;
    this.disabled = false;

    // Auto-added products tracking (variant IDs)
    this.autoAddedProducts = this.loadAutoAddedProducts();

    // Conflict detection
    this.detectConflicts();

    // Event listeners
    this.attachEventListeners();

    // Initial synchronization (after event subscription)
    // Fetch fresh cart state and sync
    this.fetchCurrentCart().then(() => {
      this.checkAndSync();
      // Update UI after load
      this.updateCartItemsUI();
    });

  }

  /**
   * ============================================
   * PARSING METHODS
   * ============================================
   */

  parseRules() {
    const rulesString = this.dataset.autoAddRules || '';
    if (!rulesString) {
      console.warn('[Auto-Add Products] No rules configured');
      return [];
    }

    return rulesString.split(',')
      .map(rule => {
        const [triggerId, linkedVariantId] = rule.split(':');
        return {
          triggerId: parseInt(triggerId),
          linkedVariantId: parseInt(linkedVariantId)
        };
      })
      .filter(rule => rule.triggerId && rule.linkedVariantId);
  }

  parseAvailability() {
    const availabilityString = this.dataset.autoAddAvailability || '';
    if (!availabilityString) {
      console.warn('[Auto-Add Products] No availability data');
      return new Map();
    }

    const availabilityMap = new Map();

    availabilityString.split(',').forEach(item => {
      const [variantId, isAvailable] = item.split(':');
      if (variantId && isAvailable !== undefined) {
        availabilityMap.set(
          parseInt(variantId),
          isAvailable === 'true'
        );
      }
    });

    return availabilityMap;
  }

  parseMetadata() {
    const metadataString = this.dataset.autoAddMetadata || '';
    if (!metadataString) {
      console.warn('[Auto-Add Products] No metadata');
      return new Map();
    }

    const metadataMap = new Map();

    // Format: variantId|title|inventoryQty|inventoryPolicy|requiresPlan|minQty|increment
    metadataString.split(';').forEach(item => {
      const parts = item.split('|');
      if (parts.length >= 7) {
        const variantId = parseInt(parts[0]);
        metadataMap.set(variantId, {
          title: parts[1],
          inventoryQty: parseInt(parts[2]),
          inventoryPolicy: parts[3],
          requiresPlan: parts[4] === 'true',
          minQty: parseInt(parts[5]),
          increment: parseInt(parts[6])
        });
      }
    });

    return metadataMap;
  }

  parseTriggerProducts() {
    const productsString = this.dataset.triggerProducts || '';
    if (!productsString) return [];

    return productsString.split(',')
      .map(id => parseInt(id))
      .filter(id => !isNaN(id) && id > 0);
  }

  parseLinkedProducts() {
    const productsString = this.dataset.linkedProducts || '';
    if (!productsString) return [];

    return productsString.split(',')
      .map(id => parseInt(id))
      .filter(id => !isNaN(id) && id > 0);
  }

  /**
   * ============================================
   * VALIDATION METHODS
   * ============================================
   */

  validateConfiguration() {
    if (this.rules.length === 0) {
      console.error('[Auto-Add Products] No valid rules configured');
      return false;
    }

    let isValid = true;

    this.rules.forEach((rule, index) => {
      // ID validation check
      if (!Number.isInteger(rule.triggerId) || !Number.isInteger(rule.linkedVariantId)) {
        console.error(`[Auto-Add Products] Rule #${index + 1}: Invalid IDs`, rule);
        isValid = false;
      }

      if (rule.triggerId <= 0 || rule.linkedVariantId <= 0) {
        console.error(`[Auto-Add Products] Rule #${index + 1}: IDs must be positive`, rule);
        isValid = false;
      }

      // Metadata check
      const meta = this.metadata.get(rule.linkedVariantId);
      if (!meta) {
        console.warn(`[Auto-Add Products] Rule #${index + 1}: No metadata for variant ${rule.linkedVariantId}`);
      }
    });

    // Duplicate check
    const triggerIds = this.rules.map(r => r.triggerId);
    const uniqueTriggers = new Set(triggerIds);
    if (uniqueTriggers.size !== triggerIds.length) {
      console.warn('[Auto-Add Products] Duplicate trigger products detected');
    }

    return isValid;
  }

  isLinkedProductAvailable(linkedVariantId) {
    const isAvailable = this.availability.get(linkedVariantId);

    if (isAvailable === undefined) {
      console.warn(`[Auto-Add Products] No availability info for variant ${linkedVariantId}, assuming unavailable`);
      return false;
    }

    return isAvailable;
  }

  checkInventory(linkedVariantId) {
    const meta = this.metadata.get(linkedVariantId);
    if (!meta) return true;

    if (meta.inventoryPolicy === 'deny' && meta.inventoryQty < 1) {
      console.warn(`[Auto-Add Products] Variant ${linkedVariantId} out of stock (${meta.inventoryQty} available)`);
      return false;
    }

    return true;
  }

  checkQuantityRules(linkedVariantId) {
    const meta = this.metadata.get(linkedVariantId);
    if (!meta) return true;

    if (meta.minQty > 1) {
      console.error(`[Auto-Add Products] Variant ${linkedVariantId} requires minimum ${meta.minQty} items`);
      return false;
    }

    if (meta.increment > 1) {
      console.warn(`[Auto-Add Products] Variant ${linkedVariantId} requires increments of ${meta.increment}`);
      return false;
    }

    return true;
  }

  checkSellingPlan(linkedVariantId) {
    const meta = this.metadata.get(linkedVariantId);
    if (!meta) return true;

    if (meta.requiresPlan) {
      console.error(`[Auto-Add Products] Variant ${linkedVariantId} requires selling plan (subscription)`);
      return false;
    }

    return true;
  }

  validateCartLimits(cart) {
    const MAX_LINE_ITEMS = 300;
    const MAX_TOTAL_QUANTITY = 10000;

    if (!cart || !cart.items) {
      console.warn('[Auto-Add Products] Invalid cart object');
      return false;
    }

    if (cart.items.length >= MAX_LINE_ITEMS) {
      console.error('[Auto-Add Products] Cart has reached maximum line items (300)');
      this.showNotification('error', 'Cart is full. Cannot add more items.');
      return false;
    }

    const totalQuantity = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    if (totalQuantity >= MAX_TOTAL_QUANTITY) {
      console.error('[Auto-Add Products] Cart has reached maximum total quantity (10000)');
      this.showNotification('error', 'Cart has too many items.');
      return false;
    }

    return true;
  }

  /**
   * ============================================
   * CORE LOGIC
   * ============================================
   */

  checkAndSync() {
    if (this.disabled) {
      return;
    }

    if (this.isProcessing) {
      this.updateQueue.add(() => this.checkAndSync());
      return;
    }

    // Loop protection
    if (!this.loopProtection.canUpdate()) {
      console.error('[Auto-Add Products] Loop protection triggered, disabling component');
      this.disabled = true;
      return;
    }

    const updates = {};
    let needsUpdate = false;
    const unavailableProducts = [];

    this.rules.forEach((rule) => {
      const hasTrigger = this.triggerProductsInCart.includes(rule.triggerId);
      const hasLinked = this.linkedProductsInCart.includes(rule.linkedVariantId);
      const isAvailable = this.isLinkedProductAvailable(rule.linkedVariantId);
      const meta = this.metadata.get(rule.linkedVariantId);

      // Scenario 1: Has trigger, no linked → ADD
      if (hasTrigger && !hasLinked) {
        // All validations
        if (!isAvailable) {
          unavailableProducts.push({ variantId: rule.linkedVariantId, meta });
        } else if (!this.checkInventory(rule.linkedVariantId)) {
          unavailableProducts.push({ variantId: rule.linkedVariantId, meta });
        } else if (!this.checkQuantityRules(rule.linkedVariantId)) {
          // Quantity rules not met
        } else if (!this.checkSellingPlan(rule.linkedVariantId)) {
          // Requires subscription
        } else {
          updates[rule.linkedVariantId] = 1;
          needsUpdate = true;
          // IMPORTANT: Mark this product as auto-added
          this.markAsAutoAdded(rule.linkedVariantId);
        }
      }

      // Scenario 2: No trigger, has linked → REMOVE (only if was auto-added)
      else if (!hasTrigger && hasLinked) {
        // CRITICAL: Remove ONLY if product was auto-added
        if (this.isAutoAdded(rule.linkedVariantId)) {
          updates[rule.linkedVariantId] = 0;
          needsUpdate = true;
          // Remove from tracking after removal
          this.unmarkAsAutoAdded(rule.linkedVariantId);
        } else {
        }
      }

      // Scenario 3: Both present → CHECK QUANTITY AND STATE
      else if (hasTrigger && hasLinked) {
        if (!isAvailable || !this.checkInventory(rule.linkedVariantId)) {
          console.warn(`[Auto-Add Products] Product became unavailable, removing ${rule.linkedVariantId}`);
          updates[rule.linkedVariantId] = 0;
          needsUpdate = true;
          unavailableProducts.push({ variantId: rule.linkedVariantId, meta });
        } else {
          // CRITICAL: If Product B was added manually, and then Product A was added
          // Mark Product B as auto-added and lock
          if (!this.isAutoAdded(rule.linkedVariantId)) {
            this.markAsAutoAdded(rule.linkedVariantId);
          }

          // IMPORTANT: Check current quantity before update
          const currentQty = this.getLinkedProductQuantity(rule.linkedVariantId);
          if (currentQty !== 1) {
            updates[rule.linkedVariantId] = 1;
            needsUpdate = true;
          } else {
          }
        }
      }
    });

    // Show notifications for unavailable products
    if (unavailableProducts.length > 0) {
      this.showUnavailableNotification(unavailableProducts);
    }

    if (needsUpdate) {
      this.updateCart(updates);
    } else {
    }
  }

  /**
   * ============================================
   * CART API
   * ============================================
   */

  updateCart(updates) {
    if (Object.keys(updates).length === 0) {
      return;
    }

    this.isProcessing = true;

    const sectionsToUpdate = this.getSectionsToUpdate();

    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        updates: updates,
        sections: sectionsToUpdate
      })
    };


    fetch(window.MinimogSettings.routes.cart_update_url, config)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(async response => {

        if (response.errors) {
          console.error('[Auto-Add Products] Cart update errors:', response.errors);
          this.handleCartErrors(response.errors);
        }

        let cartWithSections = response;

        // If sections are missing, fetch them separately
        if (!response.sections || Object.keys(response.sections || {}).length === 0) {
          try {
            const sectionsToFetch = this.getSectionsToUpdate();

            if (sectionsToFetch.length > 0) {
              const sectionsUrl = `${window.Shopify.routes.root}?sections=${sectionsToFetch.join(',')}`;
              const sectionsResponse = await fetch(sectionsUrl);
              if (sectionsResponse.ok) {
                const sectionsData = await sectionsResponse.json();
                cartWithSections.sections = sectionsData;
              }
            }
          } catch (e) {
            console.warn('[Auto-Add Products] Could not fetch sections:', e);
          }
        }

        // Update UI
        if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { cart: cartWithSections });
        }

        // IMPORTANT: Mark event as created by us, to prevent re-check trigger
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              cart: cartWithSections,
              fromAutoAdd: true  // Flag that this is our update
            }
          })
        );

        this.isProcessing = false;
      })
      .catch(error => {
        console.error('[Auto-Add Products] Update failed:', error);
        this.isProcessing = false;

        this.showNotification('error', 'Failed to update cart. Please refresh the page.');
      });
  }

  getSectionsToUpdate() {
    let sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections: sections }
      })
    );
    return sections;
  }

  handleCartErrors(errors) {
    console.error('[Auto-Add Products] Cart API errors:', errors);

    const errorMessage = typeof errors === 'string'
      ? errors
      : (errors.message || 'Cart update error');

    this.showNotification('error', errorMessage);
  }

  /**
   * ============================================
   * EVENT LISTENERS
   * ============================================
   */

  attachEventListeners() {
    // Event 1: Cart updated (main update event)
    document.addEventListener('cart:updated', this.onCartUpdated.bind(this));

    // Event 2: Quantity change (intercept qty changes)
    document.addEventListener('change', this.onQuantityChange.bind(this), true);

    // Event 3: Cart refresh
    document.addEventListener('cart:refresh', this.onCartRefresh.bind(this));

    // Event 4: Product added to cart (AJAX add to cart)
    document.addEventListener('product-ajax:added', this.onProductAdded.bind(this));

  }

  onProductAdded() {
    // CRITICAL: First update cart state, then sync
    setTimeout(() => {
      if (!this.disabled) {
        this.fetchCurrentCart().then(() => {
          this.checkAndSync();
        });
      }
    }, 300);
  }

  onCartUpdated(event) {
    if (!event.detail || !event.detail.cart) return;

    // CRITICAL: Ignore self-created events, to prevent infinite loop
    if (event.detail.fromAutoAdd) {
      const cart = event.detail.cart;
      this.updateLocalState(cart);
      // Update UI after our update
      setTimeout(() => this.updateCartItemsUI(), 100);
      return;  // Do NOT call checkAndSync!
    }

    const cart = event.detail.cart;

    // IMPORTANT: If cart is empty, reset loop protection and re-enable component
    if (!cart.items || cart.items.length === 0) {
      this.loopProtection.reset();
      this.disabled = false;
    }

    this.updateLocalState(cart);

    // Update UI after external update
    setTimeout(() => this.updateCartItemsUI(), 50);

    // Check sync with delay
    setTimeout(() => {
      if (!this.isProcessing && !this.disabled) {
        this.checkAndSync();
      }
    }, 100);
  }

  onQuantityChange(event) {
    const target = event.target;

    if (!target || !target.matches('[data-quantity-variant-id]')) {
      return;
    }

    // Skip locked inputs
    if (target.hasAttribute('data-locked-quantity')) {
      return;
    }

    const variantId = parseInt(target.dataset.quantityVariantId);

    // CRITICAL: Block quantity change ONLY for auto-added products
    const isAutoAddedProduct = this.isAutoAdded(variantId);

    if (isAutoAddedProduct) {
      console.warn('[Auto-Add Products] Blocked quantity change for auto-added product:', variantId);

      event.preventDefault();
      event.stopImmediatePropagation();

      target.value = 1;

      this.forceQuantityToOne(variantId);
      this.showLockedNotification();
    }
  }

  forceQuantityToOne(variantId) {
    const updates = {};
    updates[variantId] = 1;
    this.updateCart(updates);
  }

  onCartRefresh() {
    setTimeout(() => {
      if (!this.disabled) {
        this.checkAndSync();
      }
    }, 200);
  }

  updateLocalState(cart) {
    if (!cart || !cart.items) return;

    // IMPORTANT: Use Set to ensure unique IDs (no duplicates)
    const triggerIds = cart.items
      .filter(item => this.rules.some(r => r.triggerId === item.product_id))
      .map(item => item.product_id);
    this.triggerProductsInCart = [...new Set(triggerIds)];

    const linkedIds = cart.items
      .filter(item => this.rules.some(r => r.linkedVariantId === item.variant_id))
      .map(item => item.variant_id);
    this.linkedProductsInCart = [...new Set(linkedIds)];

    this.cartItemsCount = cart.item_count;

    // Store current cart state for quantity check
    this.currentCart = cart;

    // IMPORTANT: Clean up tracking for removed products
    this.cleanupAutoAddedTracking(cart);
  }

  /**
   * Get current quantity linked product in cart
   */
  getLinkedProductQuantity(linkedVariantId) {
    if (!this.currentCart || !this.currentCart.items) {
      return 0;
    }

    const item = this.currentCart.items.find(
      item => item.variant_id === linkedVariantId
    );

    return item ? item.quantity : 0;
  }

  /**
   * Fetch current cart state from server
   */
  async fetchCurrentCart() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }

      const cart = await response.json();
      this.updateLocalState(cart);

      return cart;
    } catch (error) {
      console.error('[Auto-Add Products] Failed to fetch cart:', error);
      return null;
    }
  }

  /**
   * ============================================
   * AUTO-ADDED PRODUCTS TRACKING
   * ============================================
   */

  /**
   * Load list auto-added products from localStorage
   */
  loadAutoAddedProducts() {
    try {
      const stored = localStorage.getItem('minimog_auto_added_products');
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Set(parsed);
      }
    } catch (error) {
      console.warn('[Auto-Add Products] Failed to load auto-added products:', error);
    }
    return new Set();
  }

  /**
   * Save list auto-added products to localStorage
   */
  saveAutoAddedProducts() {
    try {
      const array = Array.from(this.autoAddedProducts);
      localStorage.setItem('minimog_auto_added_products', JSON.stringify(array));
    } catch (error) {
      console.warn('[Auto-Add Products] Failed to save auto-added products:', error);
    }
  }

  /**
   * Mark product as auto-added
   */
  markAsAutoAdded(variantId) {
    this.autoAddedProducts.add(variantId);
    this.saveAutoAddedProducts();
  }

  /**
   * Remove product from list auto-added
   */
  unmarkAsAutoAdded(variantId) {
    this.autoAddedProducts.delete(variantId);
    this.saveAutoAddedProducts();
  }

  /**
   * Check if product was auto-added
   */
  isAutoAdded(variantId) {
    return this.autoAddedProducts.has(variantId);
  }

  /**
   * Clean up tracking for products no longer in cart
   */
  cleanupAutoAddedTracking(cart) {
    const variantsInCart = new Set(cart.items.map(item => item.variant_id));

    // Remove from tracking all products not in cart
    for (const variantId of this.autoAddedProducts) {
      if (!variantsInCart.has(variantId)) {
        this.autoAddedProducts.delete(variantId);
      }
    }

    this.saveAutoAddedProducts();
  }

  /**
   * ============================================
   * UI UPDATES
   * ============================================
   */

  /**
   * Update UI for cart items - lock ONLY auto-added products
   */
  updateCartItemsUI() {

    // Find all quantity containers with data-linked-variant-id
    const quantityContainers = document.querySelectorAll('[data-linked-variant-id]');

    quantityContainers.forEach(container => {
      const variantId = parseInt(container.dataset.linkedVariantId);

      // Check if this product auto-added
      const isAutoAdded = this.isAutoAdded(variantId);

      if (isAutoAdded) {
        // LOCK: Add locked class and disabled attributes
        this.lockQuantityInput(container, variantId);
      } else {
        // UNLOCK: Remove locked class and disabled attributes
        this.unlockQuantityInput(container, variantId);
      }
    });
  }

  /**
   * Lock quantity input for auto-added product
   */
  lockQuantityInput(container, variantId) {

    // Add locked class
    container.classList.add('m-cart-drawer-item__quantity--locked');

    // Find m-quantity or m-quantity-input element
    const quantityEl = container.querySelector('m-quantity, m-quantity-input');
    if (!quantityEl) {
      console.warn('[Auto-Add Products] m-quantity element not found for variant:', variantId);
      return;
    }

    // Lock buttons
    const buttons = quantityEl.querySelectorAll('.m-quantity__button');
    buttons.forEach(btn => {
      btn.disabled = true;
      btn.classList.add('m-quantity__button--disabled');
    });

    // Lock input
    const input = quantityEl.querySelector('.m-quantity__input');
    if (input) {
      input.disabled = true;
      input.readOnly = true;
      input.classList.add('m-quantity__input--locked');
      input.dataset.lockedQuantity = 'true';
    }

    // Add badge if enabled
    if (this.showBadge) {
      this.addAutoAddedBadge(quantityEl, variantId);
    }
  }

  /**
   * Unlock quantity input for manually added product
   */
  unlockQuantityInput(container, variantId) {

    // Remove locked class
    container.classList.remove('m-cart-drawer-item__quantity--locked');

    // Find m-quantity or m-quantity-input element
    const quantityEl = container.querySelector('m-quantity, m-quantity-input');
    if (!quantityEl) {
      console.warn('[Auto-Add Products] m-quantity element not found for variant:', variantId);
      return;
    }

    // Unlock buttons
    const buttons = quantityEl.querySelectorAll('.m-quantity__button');
    buttons.forEach(btn => {
      btn.disabled = false;
      btn.classList.remove('m-quantity__button--disabled');
    });

    // Unlock input
    const input = quantityEl.querySelector('.m-quantity__input');
    if (input) {
      input.disabled = false;
      input.readOnly = false;
      input.classList.remove('m-quantity__input--locked');
      delete input.dataset.lockedQuantity;
    }

    // Remove badge
    this.removeAutoAddedBadge(quantityEl);
  }

  /**
   * Add badge "Auto-added"
   */
  addAutoAddedBadge(quantityEl, variantId) {
    // Check if badge already exists
    if (quantityEl.querySelector('.m-cart-item__auto-added-badge')) {
      return;
    }

    const badge = document.createElement('span');
    badge.className = 'm-cart-item__auto-added-badge';
    badge.dataset.autoAddedBadge = variantId;
    badge.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
        <path d="M10.5 3L4.5 9L1.5 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
      </svg>
      <span>Auto-added</span>
    `;

    quantityEl.appendChild(badge);
  }

  /**
   * Remove badge "Auto-added"
   */
  removeAutoAddedBadge(quantityEl) {
    const badge = quantityEl.querySelector('.m-cart-item__auto-added-badge');
    if (badge) {
      badge.remove();
    }
  }

  /**
   * ============================================
   * NOTIFICATIONS
   * ============================================
   */

  showNotification(type, message) {
    if (!this.showNotifications) return;

    if (window.MinimogTheme && window.MinimogTheme.Notification) {
      window.MinimogTheme.Notification.show({
        type: type,
        message: message,
        last: 3000
      });
    } else {
    }
  }

  showUnavailableNotification(unavailableProducts) {
    if (!this.showNotifications) return;

    const products = unavailableProducts.map(p => p.meta?.title || 'Product').join(', ');
    const message = unavailableProducts.length === 1
      ? `${products} is currently unavailable`
      : `Some products are currently unavailable: ${products}`;

    this.showNotification('warning', message);
  }

  showLockedNotification() {
    if (!this.showNotifications) return;

    this.showNotification('warning', 'This item\'s quantity is locked to 1 piece');
  }

  /**
   * ============================================
   * CONFLICT DETECTION
   * ============================================
   */

  detectConflicts() {
    const conflicts = [];

    // Check gift wrapping
    const giftWrapComponent = document.querySelector('m-gift-wrapping-component');
    if (giftWrapComponent) {
    }

    // Check other auto-add scripts
    const otherAutoAdd = document.querySelectorAll('[data-auto-add]:not(m-auto-add-products)');
    if (otherAutoAdd.length > 0) {
      conflicts.push('Other auto-add scripts');
    }

    if (conflicts.length > 0) {
      console.warn('[Auto-Add Products] Potential conflicts detected:', conflicts);
    }
  }
}

/**
 * ============================================
 * HELPER CLASSES
 * ============================================
 */

class InfiniteLoopProtection {
  constructor() {
    this.updateHistory = [];
    this.MAX_UPDATES_PER_MINUTE = 20; // Increased from 10 to 20
    this.DETECTION_WINDOW = 60000;
  }

  canUpdate() {
    const now = Date.now();

    this.updateHistory = this.updateHistory.filter(
      timestamp => now - timestamp < this.DETECTION_WINDOW
    );

    if (this.updateHistory.length >= this.MAX_UPDATES_PER_MINUTE) {
      console.error('[Auto-Add Products] INFINITE LOOP DETECTED!');
      console.error('[Auto-Add Products] Updates in last minute:', this.updateHistory.length);
      return false;
    }

    this.updateHistory.push(now);
    return true;
  }

  reset() {
    this.updateHistory = [];
  }
}

class UpdateQueue {
  constructor() {
    this.queue = [];
    this.processing = false;
  }

  async add(fn) {
    this.queue.push(fn);
    if (!this.processing) {
      await this.process();
    }
  }

  async process() {
    this.processing = true;
    while (this.queue.length > 0) {
      const fn = this.queue.shift();
      try {
        await fn();
      } catch (error) {
        console.error('[Auto-Add Products] Queue error:', error);
      }
      await this.delay(300);
    }
    this.processing = false;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Register component
customElements.define('m-auto-add-products', AutoAddProductsComponent);
